import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Registar, type Kolona } from "../../../components/Registar";
import { Select } from "../../../components/Select";
import { DateRangeFilter } from "../../../components/DateRangeFilter";
import { ROLLING_PRESETS } from "../../../lib/datePresets";
import {
  workQueueApi,
  type LotQueueRow,
  type QueueProduct,
  type QualityPhase,
} from "../../../api/quality";
import { phaseLabel } from "./QualityPhaseLabels";
import { formatDate } from "../../../lib/format";
import { localizedName } from "../../../lib/localizedName";

/**
 * Radni red laboratorije: na kojim lotovima propisan test još nema nalaz.
 *
 * Svaki drugi ekran modula otvara se IZ lota — nalazi i pokrivenost oba traže oznaku šarže, a red za
 * verifikaciju pokazuje šta šef treba da potpiše, ne šta treba da se izmeri. Laborant je do sada
 * saznavao koji lot čeka iz Viber grupe (KK28), i to je korak koji ovaj ekran zamenjuje.
 *
 * Prozor datuma je deo odgovora, ne udobnost: u registru je 64.436 lotova i nijedan nalaz, pa bi red
 * bez prozora ispisao svaki lot ikada proizveden. Podrazumevano ide zadnjih mesec dana.
 *
 * Dve stvari koje red NE pokriva stoje uz broj redova, jer bi inače bile ćutanje: lotovi koje nijedan
 * plan na snazi ne pokriva (plan važi od datuma, a jedini na snazi počinje 02.10.2026), i lotovi sa
 * datumom u budućnosti — registar drži i dobavljačeve lotove kupljenih sastojaka, gde je taj datum rok.
 */

const FAZE: QualityPhase[] = ["INTAKE", "PROCESS", "SEMI_FINISHED", "FINISHED", "DISPATCH"];

function danPre(dana: number): string {
  const d = new Date();
  d.setDate(d.getDate() - dana);
  return d.toISOString().slice(0, 10);
}

/**
 * Starost u danima. Negativna znači datum u budućnosti — kod kupljenih sastojaka `produced_on`
 * nosi rok, pa se takav red ne broji kao „čeka", nego se imenuje onim što jeste.
 */
function danaCeka(datum: string): number {
  const d = new Date(datum + "T00:00:00");
  const danas = new Date();
  danas.setHours(0, 0, 0, 0);
  return Math.round((danas.getTime() - d.getTime()) / 86400000);
}

/** Koliko šarža čeka, kao stepen — boje stoje u `styles.css`, uz ostatak sistema. */
function stepenStarosti(dana: number): string {
  if (dana < 0) return "vanroka";
  if (dana <= 3) return "sveze";
  if (dana <= 10) return "stoji";
  return "dugo";
}

/**
 * Oznaka šarže je kod, ne reč: `101026E1A12525` je datum, pogon, linija i artikal spojeni. Laborant
 * je prepisuje na teglicu i čita slovo pogona iz nje. Razdvojena po svojim delovima čita se bez
 * greške; kad ne odgovara obrascu, ispisuje se doslovno — oblik se ne izmišlja.
 */
function segmenti(oznaka: string): ReactNode {
  const m = /^(\d{6})([EFef]\d?)(A\d+)$/i.exec(oznaka);
  if (!m) return oznaka;
  // Razmak je margina u `styles.css`, ne znak u tekstu: napisan kao znak, ušao bi u ono što se
  // kopira, pa bi oznaka zalepljena u pretragu glasila „100726 E4 A12525" i ne bi našla ništa.
  return (
    <>
      <span>{m[1]}</span>
      <span>{m[2].toUpperCase()}</span>
      <span>{m[3].toUpperCase()}</span>
    </>
  );
}


export default function QualityWorkQueuePage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "sr";
  const [od, setOd] = useState(danPre(30));
  const [doDana, setDoDana] = useState(new Date().toISOString().slice(0, 10));
  const [faza, setFaza] = useState<QualityPhase>("FINISHED");
  const [trazeno, setTrazeno] = useState("");
  const [upit, setUpit] = useState("");
  const [roba_id, setRoba] = useState("");
  const [robe, setRobe] = useState<QueueProduct[]>([]);

  const kljucFiltera = `${od}|${doDana}|${faza}|${upit}|${roba_id}`;

  // Spisak robe prati prozor: nudi se samo ono cega u njemu ima. Kad izabrana roba ispadne iz
  // novog prozora, izbor se skida — inace bi spisak ostao prazan uz filter koji se vise ne vidi.
  useEffect(() => {
    let otkazano = false;
    workQueueApi
      .products(od, doDana)
      .then((r) => {
        if (otkazano) return;
        setRobe(r);
        setRoba((prethodna) => (prethodna && !r.some((x) => x.erpProductId === prethodna) ? "" : prethodna));
      })
      .catch(() => {
        if (!otkazano) setRobe([]);
      });
    return () => {
      otkazano = true;
    };
  }, [od, doDana]);

  // DVE kolone, ne četiri. Red je radni nalog a ne spisak parova: levo ono po čemu se šarža
  // prepoznaje i uzima, desno ono što duguje. Na uskom ekranu `Registar` red pretvara u karticu, pa
  // je svaka kolona još jedan red sa natpisom — zato naslovna ćelija natpis i ne nosi.
  const kolone = useMemo<Kolona<LotQueueRow>[]>(
    () => [
      {
        kljuc: "sarza",
        zaglavlje: t("queue.col.lot", "Šarža"),
        labela: t("queue.col.lot", "Šarža"),
        naslovna: true,
        crtaj: (r) => {
          const glavna = r.barcode ?? r.designation ?? "—";
          const dana = danaCeka(r.producedOn);
          return (
            <div style={{ minWidth: 0 }}>
              {/* `mono` nosi monospace i veličinu sistema (`.tbl td.mono`), `b` isticanje
                  (`.tbl td b`). Pečat dodaje samo okvir i podlogu. */}
              <div
                className={`obrada-pecat mono ${stepenStarosti(dana)}`}
                title={glavna}
                data-oznaka={glavna}
              >
                <b>{segmenti(glavna)}</b>
              </div>
              <div className="obrada-roba">{r.productName ?? "—"}</div>
              <div className="obrada-meta sub">
                <b data-datum>{formatDate(r.producedOn)}</b>
                {" · "}
                {dana < 0
                  ? t("queue.meta.future", "datum u budućnosti")
                  : t("queue.meta.waiting", "čeka {{dana}} dana", { dana })}
                {" · "}
                {localizedName({ name: r.planCode, nameI18n: r.planNameI18n }, lang)}
              </div>
            </div>
          );
        },
      },
      {
        kljuc: "duguje",
        zaglavlje: t("queue.col.missing", "Bez nalaza"),
        labela: t("queue.col.missing", "Bez nalaza"),
        broj: true,
        crtaj: (r) => (
          <div style={{ textAlign: "right" }}>
            <b>{r.missing}</b>
            <div className="obrada-meta sub">
              {r.prescribed === r.missing
                ? t("queue.col.missing", "Bez nalaza")
                : t("queue.ofPrescribed", "od {{n}} propisanih", { n: r.prescribed })}
            </div>
          </div>
        ),
      },
    ],
    [t, lang],
  );

  return (
    <Registar<LotQueueRow>
      naslov={t("queue.title", "Za obradu")}
      podnaslov={t("queue.subtitle", "Šarže na kojima propisan test još nema nalaz.")}
      filteri={
        <>
          <DateRangeFilter
            from={od}
            to={doDana}
            onChange={(f, t2) => {
              setOd(f);
              setDoDana(t2);
            }}
            preseci={ROLLING_PRESETS}
            moze_bez={false}
          />
          <Select
            value={faza}
            onChange={(v) => setFaza(v as QualityPhase)}
            options={FAZE.map((f) => ({ value: f, label: phaseLabel(t, f) }))}
          />
          <Select
            value={roba_id}
            onChange={setRoba}
            options={[
              { value: "", label: t("queue.allProducts", "Sva roba") },
              ...robe.map((r) => ({ value: r.erpProductId, label: r.name ?? r.erpProductId })),
            ]}
          />
          <input
            className="input"
            type="search"
            value={trazeno}
            placeholder={t("queue.search", "Oznaka šarže")}
            onChange={(e) => setTrazeno(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setUpit(trazeno);
            }}
            onBlur={() => setUpit(trazeno)}
            style={{ minWidth: 190 }}
          />
        </>
      }
      kljucFiltera={kljucFiltera}
      filtrirano={Boolean(upit) || Boolean(roba_id) || faza !== "FINISHED"}
      ucitaj={(strana, velicina) =>
        workQueueApi.page(strana, velicina, {
          from: od,
          to: doDana,
          phase: faza,
          q: upit,
          product: roba_id || undefined,
        })
      }
      kolone={kolone}
      kljucReda={(r) => r.lotId}
      vezaNaRed={(r) =>
        // `q` je ono po čemu odredište TRAŽI, `lot` ono po čemu BIRA: na soji oznaka nije jedinstvena,
        // jer `designation` tamo nosi klasu, pa se pogodak razrešava po identitetu.
        `/quality/readings?${new URLSearchParams({
          lot: r.lotId,
          // Barkod, ne oznaka: na soji `designation` nosi klasu, pa se po njoj nalazi 14 od 280
          // šarži, a po barkodu svih 280.
          q: r.barcode ?? r.designation ?? "",
          plan: r.planId,
          phase: faza,
        }).toString()}`
      }
      prazno={t("queue.empty", "Nijedna šarža u ovom prozoru ne čeka obradu.")}
      praznoUzFilter={t("queue.emptyFiltered", "Nijedna šarža ne odgovara filteru.")}
    />
  );
}
